import { faPen, faPlus, faSave, faTimes, faTrash, faArrowRotateRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  ActionIcon,
  Alert,
  Button,
  Card,
  Group,
  Loader,
  Modal,
  NumberInput,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { DateTime } from "luxon";
import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { getAllPayrates } from "../../data/actions/ratesActions";
import AssociationsApi from "../../data/api/associationsApi";
import RatesApi from "../../data/api/ratesApi";
import { IState } from "../../data/reducers/state";
import { IAssociation } from "../../types/association/Association";
import { BasketballGrade } from "../../types/common/basketballGrades";
import { RefereeCoachGrade } from "../../types/common/refereeCoachGrades";
import { StatisticianGrade } from "../../types/common/statisticianGrade";
import { IPayRate, IPayrateAttachment } from "../../types/payrates/PayRate";
import { ITag, TagType } from "../../types/tags/tag";
import { toDateTimeNullable } from "../../util/date+helper";
import { CenteredLoader } from "../common/loader";

interface IPayratesViewerProps {
  associations: IAssociation[];
  payrates: { [id: string]: IPayRate };
  isAdmin?: boolean;
  effectiveDate?: DateTime;
  onEffectiveDateChange?: (value: Date | string | null) => void;
  actions: {
    getAllPayrates: () => void;
  };
  selectedAssociation?: IAssociation | null;
}

interface IEditAssociationState {
  attachment: IPayrateAttachment;
  payrate: IPayRate;
  association: IAssociation;
}

interface EditFormState {
  start: Date | null;
  finish: Date | null;
  priority: number;
  tags: ITag[];
}

const prepareTextTag = (tag: ITag) =>
  tag.type === TagType.custom ? tag.value : `${TagType.toString(tag.type)}: ${tag.value}`;

const formatCurrency = (value?: number) => {
  if (value === undefined || value === null) return "";
  return `$${value}`;
};

const ensureJsDate = (value: unknown): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === "string" || typeof value === "number") {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  if (value && typeof (value as { toJSDate?: () => Date }).toJSDate === "function") {
    try {
      return (value as { toJSDate: () => Date }).toJSDate();
    } catch {
      return null;
    }
  }
  return null;
};

const VertTD: React.FC<React.DetailedHTMLProps<React.TdHTMLAttributes<HTMLTableCellElement>, HTMLTableCellElement>> =
  ({ children, ...other }) => (<td style={{ verticalAlign: "top" }} {...other}>{children}</td>);

const AssociationPayrateDetails: React.FC<{
  payrate: IPayRate;
  associationPayrate: IPayrateAttachment;
  hideTags?: boolean;
  onEdit?: (attachment: IPayrateAttachment, payrate: IPayRate) => void;
  showAssociationBadge?: boolean;
  associationLabel?: string;
}> = ({ payrate, associationPayrate, hideTags, onEdit, showAssociationBadge, associationLabel }) => {
  const tags: React.ReactNode[] =
    associationPayrate.tags?.map((t) => (
      <span key={t.type + t.value} style={{ marginRight: "5px", fontSize: "14px", color: "#467bcf" }}>
        {prepareTextTag(t)}
      </span>
    )) || [];

  const handleEditClick = () => {
    if (onEdit) onEdit(associationPayrate, payrate);
  };

  const nameContent = onEdit ? (
    <UnstyledButton
      onClick={handleEditClick}
      style={{
        padding: 0,
        background: "none",
        border: "none",
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        gap: "0.35rem",
      }}
      aria-label={`Edit association rule for ${payrate.name}`}
    >
      <FontAwesomeIcon icon={faPen} style={{ fontSize: "0.85rem" }} />
      <span>{payrate.name}</span>
    </UnstyledButton>
  ) : (
    <span>{payrate.name}</span>
  );

  const dateLabel = React.useMemo(() => {
    const start = toDateTimeNullable(associationPayrate.start);
    const finish = toDateTimeNullable(associationPayrate.finish);
    if (start && finish) {
      return `${start.toLocaleString(DateTime.DATE_MED)} — ${finish.toLocaleString(DateTime.DATE_MED)}`;
    }
    if (start) return `From ${start.toLocaleString(DateTime.DATE_MED)}`;
    if (finish) return `Until ${finish.toLocaleString(DateTime.DATE_MED)}`;
    return "Always";
  }, [associationPayrate.start, associationPayrate.finish]);

  return (
    <>
      <Tooltip label={`${payrate._id} (priority: ${associationPayrate.priority || 100})`} position="right" multiline maw={500}>
        {nameContent}
      </Tooltip>
      {showAssociationBadge && associationLabel && (
        <Text size="xs" c="dimmed" mt={2}>
          Payrate owner: {associationLabel}
        </Text>
      )}
      <em style={{ color: "var(--color-text-disabled)" }}>
        {dateLabel}
      </em>
      {!hideTags && tags.length > 0 && (
        <div style={{ marginTop: 4 }}>
          {tags}
        </div>
      )}
    </>
  );
};

const PriorityCell: React.FC<{ priority: number; updatePriority: (n: number) => void; canEdit?: boolean }> = ({
  priority,
  updatePriority,
  canEdit,
}) => {
  const [isEditing, setEditing] = React.useState(false);
  const [editingValue, setEditingValue] = React.useState(priority);

  if (!isEditing || !canEdit) {
    return <VertTD onDoubleClick={() => canEdit && setEditing(true)}>{priority}</VertTD>;
  }

  return (
    <td>
      <input
        type="number"
        value={editingValue}
        onChange={(e) => setEditingValue(parseInt(e.currentTarget.value))}
        onBlur={(e) => {
          if (editingValue !== priority) {
            updatePriority(parseInt(e.currentTarget.value));
          }
          setEditing(false);
        }}
        style={{ width: "100%" }}
      />
    </td>
  );
};

const RoleCard: React.FC<{
  title: string;
  children: React.ReactNode;
  headers: React.ReactNode[];
  adminHint?: boolean;
}> = ({ title, children, headers, adminHint }) => (
  <Card shadow="sm" padding="md">
    <Stack gap="sm">
      <Group justify="space-between">
        <Title order={4}>{title}</Title>
        {adminHint && <Text size="xs" c="dimmed">Double-click priority to edit</Text>}
      </Group>
      <Table withColumnBorders striped>
        <thead>
          <tr>{headers.map((h, idx) => <th key={`role-card-${idx}-${h}`}>{h}</th>)}</tr>
        </thead>
        <tbody>{children}</tbody>
      </Table>
    </Stack>
  </Card>
);

const PayratesViewer: React.FC<IPayratesViewerProps> = (props) => {
  const [internalEffectiveDate, setInternalEffectiveDate] = React.useState<DateTime>(DateTime.local().startOf('day'));
  const [associationsForDate, setAssociationsForDate] = React.useState<IAssociation[] | null>(null);
  const [isLoadingAssociations, setIsLoadingAssociations] = React.useState(false);
  const [associationLoadError, setAssociationLoadError] = React.useState<string | null>(null);
  const [editState, setEditState] = React.useState<IEditAssociationState | null>(null);
  const [editForm, setEditForm] = React.useState<EditFormState | null>(null);
  const [isSavingEdit, setIsSavingEdit] = React.useState(false);
  const [editError, setEditError] = React.useState<string | null>(null);
  const effectiveDate = props.effectiveDate ?? internalEffectiveDate;
  const lastFetchedDateRef = React.useRef<string | null>(null);

  const associationsToUse = associationsForDate ?? props.associations ?? [];

  const associationNameMap = React.useMemo(
    () => new Map((associationsToUse || []).map((assoc) => [assoc._id, assoc.name])),
    [associationsToUse],
  );

  const tagTypeOptions = React.useMemo(() => {
    const numericValues = Array.from(
      new Set(Object.values(TagType).filter((value): value is TagType => typeof value === "number")),
    );
    return numericValues.map((value) => ({ value: value.toString(), label: TagType.toString(value) }));
  }, []);

  const defaultTagType = React.useMemo(() => {
    return tagTypeOptions.length > 0 ? (Number(tagTypeOptions[0].value) as TagType) : TagType.gameMode;
  }, [tagTypeOptions]);

  const loadAssociationsForDate = React.useCallback(
    async (targetDate: DateTime, force = false) => {
      if (!props.isAdmin) return;

      const fetchKey = targetDate?.toISODate() || 'today';
      if (!force && lastFetchedDateRef.current === fetchKey) return;

      setIsLoadingAssociations(true);
      setAssociationLoadError(null);
      try {
        const associations = await AssociationsApi.getAdminAssociations(targetDate.toISO());
        setAssociationsForDate(associations);
        lastFetchedDateRef.current = fetchKey;
      } catch (error: any) {
        setAssociationLoadError(error?.message || "Failed to load payrates for the selected date.");
        setAssociationsForDate(null);
      } finally {
        setIsLoadingAssociations(false);
      }
    },
    [props.isAdmin],
  );

  const handleEffectiveDateChange = (value: Date | string | null) => {
    if (!value) {
      setAssociationLoadError(null);
      const today = DateTime.local().startOf("day");
      props.onEffectiveDateChange ? props.onEffectiveDateChange(today.toJSDate()) : setInternalEffectiveDate(today);
      return;
    }

    let selected: DateTime | null = null;
    if (value instanceof Date) {
      selected = DateTime.fromJSDate(value).startOf("day");
    } else if (typeof value === "string") {
      selected = DateTime.fromISO(value).startOf("day");
    }

    if (!selected || !selected.isValid) {
      setAssociationLoadError("Invalid date selected.");
      return;
    }

    setAssociationLoadError(null);
    if (props.onEffectiveDateChange) {
      props.onEffectiveDateChange(selected.toJSDate());
    } else {
      setInternalEffectiveDate(selected);
    }
  };

  React.useEffect(() => {
    if (editState) {
      const attachment = editState.attachment;
      setEditForm({
        start: ensureJsDate(attachment.start),
        finish: ensureJsDate(attachment.finish),
        priority: attachment.priority ?? 100,
        tags: attachment.tags ? attachment.tags.map((tag) => ({ ...tag })) : [],
      });
      setEditError(null);
      setIsSavingEdit(false);
    } else {
      setEditForm(null);
      setEditError(null);
      setIsSavingEdit(false);
    }
  }, [editState]);

  React.useEffect(() => {
    props.actions.getAllPayrates();
  }, []);

  React.useEffect(() => {
    loadAssociationsForDate(effectiveDate);
  }, [effectiveDate, loadAssociationsForDate]);

  const handleManualRefresh = () => {
    loadAssociationsForDate(effectiveDate, true);
  };

  const isAttachmentActiveOnDate = React.useCallback(
    (attachment: IPayrateAttachment) => {
      const start = toDateTimeNullable(attachment.start);
      const finish = toDateTimeNullable(attachment.finish);
      return (!start || start <= effectiveDate) && (!finish || effectiveDate <= finish);
    },
    [effectiveDate],
  );

  if (!props.payrates || Object.keys(props.payrates).length === 0) {
    return <CenteredLoader />;
  }

  const updatePriority = (payrateId: string, priority: number) => {
    if (!props.isAdmin) return;
    RatesApi.updateAssociationPayrate(payrateId, { priority }).then(() => window.location.reload());
  };

  const handleEditPayrateClick = (association: IAssociation, attachment: IPayrateAttachment, payrate: IPayRate) => {
    if (!props.isAdmin) return;
    const clonedAttachment: IPayrateAttachment = {
      ...attachment,
      tags: attachment.tags ? attachment.tags.map((tag) => ({ ...tag })) : [],
    };
    setEditState({ association, payrate, attachment: clonedAttachment });
  };

  const handleAddTag = () => {
    if (!editForm) return;
    setEditForm((prev) =>
      prev
        ? {
          ...prev,
          tags: [...prev.tags, { type: defaultTagType, value: "" }],
        }
        : prev,
    );
  };

  const handleTagTypeChange = (index: number, value: string | null) => {
    if (!editForm || value === null) return;
    const parsed = Number(value);
    if (Number.isNaN(parsed)) return;
    setEditForm((prev) =>
      prev
        ? {
          ...prev,
          tags: prev.tags.map((tag, i) => (i === index ? { ...tag, type: parsed as TagType } : tag)),
        }
        : prev,
    );
  };

  const handleTagValueChange = (index: number, value: string) => {
    if (!editForm) return;
    setEditForm((prev) =>
      prev
        ? {
          ...prev,
          tags: prev.tags.map((tag, i) => (i === index ? { ...tag, value } : tag)),
        }
        : prev,
    );
  };

  const handleRemoveTag = (index: number) => {
    if (!editForm) return;
    setEditForm((prev) =>
      prev
        ? {
          ...prev,
          tags: prev.tags.filter((_, i) => i !== index),
        }
        : prev,
    );
  };

  const handleCloseEditModal = () => {
    if (isSavingEdit) return;
    setEditState(null);
  };

  const handleSaveEditedAttachment = async () => {
    if (!props.isAdmin || !editState || !editForm) return;
    setIsSavingEdit(true);
    setEditError(null);
    try {
      const priorityValue = Number.isFinite(editForm.priority) ? editForm.priority : 100;
      await RatesApi.updateAssociationPayrate(editState.attachment._id, {
        start: editForm.start ?? undefined,
        finish: editForm.finish ?? undefined,
        priority: priorityValue,
        tags: editForm.tags,
      });
      window.location.reload();
    } catch (error: any) {
      setEditError(error?.message || "Failed to update payrate rule.");
      setIsSavingEdit(false);
    }
  };

  const filteredAssociations = props.selectedAssociation
    ? associationsToUse?.filter((assoc) => assoc._id === props.selectedAssociation?._id)
    : associationsToUse;

  const formatted = filteredAssociations?.map((association) => {
    const filteredAssocPayrates = association?.payrates
      ?.filter((assocPr) => isAttachmentActiveOnDate(assocPr))
      ?.sort((p1, p2) => (p2.priority || 100) - (p1.priority || 100));

    const supervisorRows = filteredAssocPayrates
      ?.filter((pr) => !!props.payrates[pr.payrateId]?.supervisor)
      ?.map((assocPr) => {
        const payrate = props.payrates[assocPr.payrateId];
        return (
          <tr key={`supPayrate-${assocPr._id}`}>
            <td>
              <AssociationPayrateDetails
                payrate={payrate}
                associationPayrate={assocPr}
                hideTags
                onEdit={
                  props.isAdmin
                    ? (attachment, payrateToEdit) => handleEditPayrateClick(association, attachment, payrateToEdit)
                    : undefined
                }
                showAssociationBadge={props.isAdmin && !!payrate.associationId}
                associationLabel={payrate.associationId ? associationNameMap.get(payrate.associationId) : undefined}
              />
            </td>
            <PriorityCell
              priority={assocPr.priority}
              updatePriority={(priority) => updatePriority(assocPr._id, priority)}
              canEdit={props.isAdmin}
            />
            <VertTD>{formatCurrency(payrate.supervisor)}</VertTD>
          </tr>
        );
      });


    const refereeRows = filteredAssocPayrates
      ?.filter((pr) => !!props.payrates[pr.payrateId]?.referee)
      ?.map((assocPr) => {
        const payrate = props.payrates[assocPr.payrateId];
        const showPlusRate =
          payrate?.referee?.plusRate &&
          Object.keys(payrate.referee.plusRate).length > 0 &&
          Object.values(payrate.referee.plusRate).some((rate) => rate && rate > 0);

        return (
          <React.Fragment key={`refPayrate-${assocPr._id}`}>
            <tr>
              <td rowSpan={showPlusRate ? 2 : 1}>
                <AssociationPayrateDetails
                  payrate={payrate}
                  associationPayrate={assocPr}
                  onEdit={
                    props.isAdmin
                      ? (attachment, payrateToEdit) => handleEditPayrateClick(association, attachment, payrateToEdit)
                      : undefined
                  }
                  showAssociationBadge={props.isAdmin && !!payrate.associationId}
                  associationLabel={payrate.associationId ? associationNameMap.get(payrate.associationId) : undefined}
                />
              </td>
              <PriorityCell
                priority={assocPr.priority}
                updatePriority={(priority) => updatePriority(assocPr._id, priority)}
                canEdit={props.isAdmin}
              />
              <VertTD>Standard</VertTD>
              {BasketballGrade.all().map((bg) => (
                <VertTD key={bg}>{formatCurrency(payrate.referee.rate?.[bg])}</VertTD>
              ))}
            </tr>
            {showPlusRate && (
              <tr>
                <VertTD />
                <VertTD>Panel</VertTD>
                {BasketballGrade.all().map((bg) => (
                  <VertTD key={bg}>{formatCurrency(payrate.referee?.plusRate?.[bg])}</VertTD>
                ))}
              </tr>
            )}
          </React.Fragment>
        );
      });

    const scoretableRows = filteredAssocPayrates
      ?.filter((pr) => !!props.payrates[pr.payrateId]?.scoretable)
      ?.map((assocPr) => {
        const payrate = props.payrates[assocPr.payrateId];

        return (
          <tr key={`scorePayrate-${assocPr._id}`}>
            <td>
              <AssociationPayrateDetails
                payrate={payrate}
                associationPayrate={assocPr}
                onEdit={
                  props.isAdmin
                    ? (attachment, payrateToEdit) => handleEditPayrateClick(association, attachment, payrateToEdit)
                    : undefined
                }
                showAssociationBadge={props.isAdmin && !!payrate.associationId}
                associationLabel={payrate.associationId ? associationNameMap.get(payrate.associationId) : undefined}
              />
            </td>
            <PriorityCell
              priority={assocPr.priority}
              updatePriority={(priority) => updatePriority(assocPr._id, priority)}
              canEdit={props.isAdmin}
            />
            <VertTD>Standard</VertTD>
            {[0, 1, 2, 3, 4].map((bg) => (
              <VertTD key={bg}>{formatCurrency(payrate.scoretable?.rate?.[bg])}</VertTD>
            ))}
          </tr>
        );
      });

    const refereeCoachRows = filteredAssocPayrates
      ?.filter((pr) => !!props.payrates[pr.payrateId]?.refereeCoach)
      ?.map((assocPr) => {
        const payrate = props.payrates[assocPr.payrateId];

        return (
          <tr key={`refCoachPayrate-${assocPr._id}`}>
            <td>
              <AssociationPayrateDetails
                payrate={payrate}
                associationPayrate={assocPr}
                onEdit={
                  props.isAdmin
                    ? (attachment, payrateToEdit) => handleEditPayrateClick(association, attachment, payrateToEdit)
                    : undefined
                }
                showAssociationBadge={props.isAdmin && !!payrate.associationId}
                associationLabel={payrate.associationId ? associationNameMap.get(payrate.associationId) : undefined}
              />
            </td>
            <PriorityCell
              priority={assocPr.priority}
              updatePriority={(priority) => updatePriority(assocPr._id, priority)}
              canEdit={props.isAdmin}
            />
            {RefereeCoachGrade.all().map((rcg) => (
              <VertTD key={rcg}>{formatCurrency(payrate.refereeCoach.rate?.[rcg])}</VertTD>
            ))}
          </tr>
        );
      });

    const statisticianRows = filteredAssocPayrates
      ?.filter((pr) => !!props.payrates[pr.payrateId]?.refereeCoach)
      ?.map((assocPr) => {
        const payrate = props.payrates[assocPr.payrateId];

        return (
          <tr key={`statisticianRate-${assocPr._id}`}>
            <td>
              <AssociationPayrateDetails
                payrate={payrate}
                associationPayrate={assocPr}
                onEdit={
                  props.isAdmin
                    ? (attachment, payrateToEdit) => handleEditPayrateClick(association, attachment, payrateToEdit)
                    : undefined
                }
                showAssociationBadge={props.isAdmin && !!payrate.associationId}
                associationLabel={payrate.associationId ? associationNameMap.get(payrate.associationId) : undefined}
              />
            </td>
            <PriorityCell
              priority={assocPr.priority}
              updatePriority={(priority) => updatePriority(assocPr._id, priority)}
              canEdit={props.isAdmin}
            />
            {StatisticianGrade.all().map((rcg) => (
              <VertTD key={rcg}>{formatCurrency(payrate.statistician?.rate?.[rcg])}</VertTD>
            ))}
          </tr>
        );
      });

    return (
      <Stack key={association._id} gap="md">
        <Title order={3}>{association.name}</Title>

        {supervisorRows && supervisorRows.length > 0 && (
          <RoleCard
            title="Supervisor"
            headers={["", "Priority", "Rate (per hour)"]}
            adminHint={props.isAdmin}
          >
            {supervisorRows}
          </RoleCard>
        )}

        {refereeRows && refereeRows.length > 0 && (
          <RoleCard
            title="Referee"
            headers={[
              "",
              "Priority",
              "",
              ...BasketballGrade.all().map((bg) => BasketballGrade.toShortString(bg)),
            ]}
            adminHint={props.isAdmin}
          >
            {refereeRows}
          </RoleCard>
        )}

        {scoretableRows && scoretableRows.length > 0 && (
          <RoleCard
            title="Scoretable"
            headers={["", "Priority", "", ...[0, 1, 2, 3, 4].map((bg) => bg)]}
            adminHint={props.isAdmin}
          >
            {scoretableRows}
          </RoleCard>
        )}

        {refereeCoachRows && refereeCoachRows.length > 0 && (
          <RoleCard
            title="Referee Coach"
            headers={["", "Priority", ...RefereeCoachGrade.all().map((rcg) => RefereeCoachGrade.toShortString(rcg))]}
            adminHint={props.isAdmin}
          >
            {refereeCoachRows}
          </RoleCard>
        )}


      </Stack>
    );
  });

  return (
    <>
      <Stack gap="lg">
        <Group justify="space-between" align="center">
          <Stack gap={2}>
            <Title order={5}>Effective date</Title>
            <Text size="sm" color="dimmed">
              Choose a date to preview which association payrate rules will apply.
            </Text>
          </Stack>
          <Group gap="sm" align="center">
            {isLoadingAssociations && <Loader size="sm" />}
            <DatePickerInput
              value={effectiveDate?.isValid ? effectiveDate.toJSDate() : null}
              onChange={handleEffectiveDateChange}
              clearable
              aria-label="Select payrate effective date"
            />
            <Button
              variant="light"
              size="sm"
              onClick={handleManualRefresh}
              leftSection={<FontAwesomeIcon icon={faArrowRotateRight} size="sm" />}
              disabled={isLoadingAssociations}
            >
              Refresh
            </Button>
          </Group>
        </Group>

        {associationLoadError && (
          <Alert color="red" title="Unable to refresh payrates">
            {associationLoadError}
          </Alert>
        )}

        {formatted}
      </Stack>
      <Modal
        opened={Boolean(editState)}
        onClose={handleCloseEditModal}
        title={editState ? `Edit ${editState.payrate.name} for ${editState.association.name}` : undefined}
        size="lg"
        closeButtonProps={{ disabled: isSavingEdit }}
      >
        {editForm && editState ? (
          <Stack gap="md">
            {editError && (
              <Alert color="red" title="Update failed">
                {editError}
              </Alert>
            )}
            <Text size="sm" color="dimmed">
              Adjust how this payrate applies to games for <strong>{editState.association.name}</strong>. These settings
              update immediately once you save.
            </Text>

            <Group grow>
              <DatePickerInput
                label="Start date"
                placeholder="Optional start date"
                value={editForm.start}
                onChange={(value) =>
                  setEditForm((prev) =>
                    prev
                      ? {
                        ...prev,
                        start: value,
                      }
                      : prev,
                  )
                }
                clearable
                disabled={isSavingEdit}
                readOnly={!props.isAdmin}
              />
              <DatePickerInput
                label="End date"
                placeholder="Optional end date"
                value={editForm.finish}
                onChange={(value) =>
                  setEditForm((prev) =>
                    prev
                      ? {
                        ...prev,
                        finish: value,
                      }
                      : prev,
                  )
                }
                clearable
                disabled={isSavingEdit || !props.isAdmin}
              />
            </Group>

            <NumberInput
              label="Priority"
              description="Higher numbers take precedence over lower ones"
              value={editForm.priority}
              onChange={(value) =>
                setEditForm((prev) =>
                  prev
                    ? {
                      ...prev,
                      priority: value === "" || value === null || value === undefined ? 100 : Number(value),
                    }
                    : prev,
                )
              }
              min={1}
              max={999}
              step={1}
              disabled={isSavingEdit || !props.isAdmin}
            />

            <Stack gap="xs">
              <Group justify="space-between" align="center">
                <Text size="sm" fw={500}>
                  Tags / rule filters
                </Text>
                <Button
                  variant="subtle"
                  size="xs"
                  onClick={handleAddTag}
                  leftSection={<FontAwesomeIcon icon={faPlus} size="sm" />}
                  disabled={isSavingEdit || !props.isAdmin}
                >
                  Add tag
                </Button>
              </Group>

              {editForm.tags.length === 0 ? (
                <Alert color="gray" title="No tags">
                  Without tags this rule applies to all games that fall within the date range.
                </Alert>
              ) : (
                <Stack gap="xs">
                  {editForm.tags.map((tag, index) => (
                    <Group key={`${tag.type}-${index}`} align="flex-end">
                      <Select
                        label="Type"
                        placeholder="Select tag type"
                        value={String(tag.type)}
                        data={tagTypeOptions}
                        onChange={(value) => handleTagTypeChange(index, value)}
                        style={{ width: 200 }}
                        withinPortal
                        disabled={isSavingEdit || !props.isAdmin}
                      />
                      <TextInput
                        label="Value"
                        placeholder="Enter tag value"
                        value={tag.value}
                        onChange={(event) => handleTagValueChange(index, event.currentTarget.value)}
                        style={{ flexGrow: 1 }}
                        disabled={isSavingEdit || !props.isAdmin}
                      />
                      <ActionIcon
                        color="red"
                        variant="subtle"
                        onClick={() => handleRemoveTag(index)}
                        aria-label="Remove tag"
                        disabled={isSavingEdit || !props.isAdmin}
                      >
                        <FontAwesomeIcon icon={faTrash} size="sm" />
                      </ActionIcon>
                    </Group>
                  ))}
                </Stack>
              )}
            </Stack>

            <Group justify="flex-end">
              <Button
                variant="outline"
                leftSection={<FontAwesomeIcon icon={faTimes} size="sm" />}
                onClick={handleCloseEditModal}
                disabled={isSavingEdit}
              >
                Cancel
              </Button>
              <Button
                leftSection={<FontAwesomeIcon icon={faSave} size="sm" />}
                onClick={handleSaveEditedAttachment}
                loading={isSavingEdit}
                disabled={!props.isAdmin}
              >
                Save changes
              </Button>
            </Group>
          </Stack>
        ) : (
          <Stack>
            <Text size="sm" color="dimmed">
              Select a payrate to edit its association settings.
            </Text>
          </Stack>
        )}
      </Modal>
    </>
  );
};

function map2State(state: IState): Partial<IPayratesViewerProps> {
  return {
    associations: state.associations?.admin?.map((assocId) => state.associations.map[assocId]),
    payrates: state.payrates.map,
    isAdmin: true,
  };
}

function mapDispatchToProps(dispatch: any): Partial<IPayratesViewerProps> {
  return {
    actions: bindActionCreators({ getAllPayrates }, dispatch),
  };
}

export default connect(map2State, mapDispatchToProps)(PayratesViewer);
